import { is } from 'stdlib/is'
import { Object } from 'stdlib/Object'

import Logger from '../Logger/Logger'

export type TEmitter = Element
export type TData    = unknown

export interface IEvent {
  readonly emitter?: TEmitter | undefined
  readonly data?:    TData
}

// Mivel csak típussal tudtam volna megoldani, hogy vagy emitter vagy data, vagy mindkettő
// legyen kötelező, ezért létrehoztam ezt a segéd típust.

// Így nem lehet például üres objectet átadni.
type TRequireAtLeastOne<T, G extends keyof T = keyof T> = {
  [K in G]-?: Partial<Omit<T, K>> & Required<Pick<T, K>>
}[G] & Pick<T, Exclude<keyof T, G>>

type TStrictEvent = TRequireAtLeastOne<IEvent, 'emitter' | 'data'>

export type TCallback = (event: IEvent) => void | Promise<void>

export interface IEventHandler<TEvent extends string = string> {
  readonly on:    (eventName: TEvent, callback: TCallback) => void
  readonly once:  (eventName: TEvent, callback: TCallback) => void
  readonly off:   (eventName: TEvent, callback: TCallback) => void
  readonly emit:  (eventName: TEvent, event?: TStrictEvent)      => Promise<void[]>
  readonly clear: (eventName?: TEvent)                     => void
}

type TEvents<TEvent extends string> = Partial<Record<TEvent, TCallback[]>>

export default class EventHandler<TEvent extends string = string> implements IEventHandler <TEvent> {
  public static isStrict = true

  public logger = new Logger('EventHandler')
  protected events: TEvents<TEvent> = {}

  private readonly isStrict: boolean

  public get stats (): string {
    const eventNames = Object.getKeys<TEvent>(this.events)

    const callbacks = eventNames.reduce((acc, curr) => {
      return (acc + (this.events[curr]?.length ?? 0))
    }, 0)

    /** Mennyi eventre mennyi callback jut. */
    const stats = `${ eventNames.length } / ${ callbacks }`

    return stats
  }

  public constructor (isStrict = false) {
    this.isStrict = isStrict
  }

  /**
   * Fel vagyunk iratkozva az Eventre?
   * @param event - EventName.
   */
  public isSubscribedOnEvent (event: TEvent): boolean {
    return is.defined(this.events[event])
  }

  /**
   * Egy esemény hallgatása.
   * @param eventName - Az esemény neve.
   * @param callback  - A callback, ami meg lesz hívva.
   */
  public on (eventName: TEvent, callback: TCallback): void {
    if (!this.isSubscribedOnEvent(eventName)) {
      this.events[eventName] = []
    }

    this.events[eventName]?.push(callback)
  }

  /**
   * Egy esemény hallgatása, de csak egyszer.
   * Ha elsül, leiratkozik róla automatikusan.
   * @param eventName - Az esemény neve.
   * @param callback  - A callback, ami meg lesz hívva.
   */
  public once (eventName: TEvent, callback: TCallback): void {
    /**
     * Dummy callback, amiről egyből le is iratkozunk.
     * @param event - Az esemény object, amit tovább passzolunk.
     */
    const cb = (event: IEvent): void => { // eslint-disable-line no-restricted-syntax -- A this miatt kell.
      callback(event)
      this.off(eventName, cb)
    }

    this.on(eventName, cb)
  }

  /**
   * Leiratkozás egy függvényről.
   * @param eventName         - Az esemény neve, amiről leiratkozunk.
   * @param removableCallback - A callback, amit leveszünk.
   */
  public off (eventName: TEvent, removableCallback: TCallback): void {
    const evs = this.events[eventName]

    if (!evs) {
      return
    }

    const remainingCallbacks = evs.filter((callback) => {
      return callback !== removableCallback
    })

    this.events[eventName] = remainingCallbacks
  }

  /**
   * Egy esemény kilövése.
   * @param eventName - Az esemény, amit kilövünk.
   * @param event     - Az esemény object, amit küldünk.
   * @throws Strict módban, ha az adott eseményhez nem tartozik callback, akkor hibát dobunk.
   */
  public emit (eventName: TEvent, event?: IEvent): Promise<void[]> {
    const ev = event ?? { emitter: undefined, data: undefined }

    this.logger.dev(eventName, ev)

    const evs = this.events[eventName] ?? []

    if (!is.empty(evs)) {
      return Promise.all(
        evs.map((cb) => {
          const res = cb(ev)

          if (is.instanceOf(res, Promise)) {
            return res
          }

          return Promise.resolve()
        })
      )
    }
    else if (this.isStrict) {
      throw new TypeError(`[EventHandler] a(z) ${ eventName } eseményhez nem tartozik callback!`)
    }

    return Promise.resolve([])
  }

  /**
   * Egy vagy az összes eseményhez tartozó callback-ek törlése.
   * @param eventName - Az esemény neve opcionális.
   */
  public clear (eventName?: TEvent): void {
    if (eventName) {
      this.clearEvent(eventName)
    }
    else {
      Object.getKeys(this.events).forEach((ev) => {
        this.clearEvent(ev)
      })
    }
  }

  /**
   * Egy esemény törlése.
   * @param eventName - Az esemény neve.
   */
  private clearEvent (eventName: TEvent): void {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- Itt szükséges törölni.
    delete this.events[eventName]
  }
}
